<script setup lang="ts">
import { computed, onMounted, ref, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { storeToRefs } from 'pinia'
import Menu from 'primevue/menu'
import Dialog from 'primevue/dialog'
import type { MenuItem } from 'primevue/menuitem'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppDarkMode from '@/components/molecules/dark-mode/AppDarkMode.vue'
import AppThemeSwitcher from '@/components/molecules/theme/AppThemeSwitcher.vue'
import AppOptionSwitcher from '../notes/AppOptionSwitcher.vue'
import { useCampaignRegistrationSync } from '@/composables/useCampaignRegistrationSync'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import type { ActivePauseSelection } from '@/stores/usePauseStatusStore'
import { usePauseCreateConfirmDialog } from '@/composables/usePauseCreateConfirmDialog'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { useCallService } from '@/services/call/useCallService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useChatService } from '@/services/chat/useChatService'
import { usePauseService } from '@/services/pause/usePauseService'
import { useLogoutConfirm } from '@/components/atoms/sidebars/composables/useLogoutConfirm'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import IconInfo from '@/assets/svg/IconInfo.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import IconIncomingArrowCircle from '@/assets/svg/iconIncomingArrowCircle.svg'
import IconExitArrowCircle from '@/assets/svg/iconExitArrowCircle.svg'
import AppNotificationSettings from '@/components/molecules/notifications/AppNotificationSettings.vue'
import IconWaitingTime from '@/assets/svg/iconWaitingTime.svg'
import type { ButtonActions } from './interface/buttonActions'
import type {
  IOperatorSessionCall,
  IOperatorSessionCallUnregister,
  IRegisterOperatorSession,
  IRegisterQueues,
  IRegisterWhatsapp,
  IUnregisterOperatorSession,
  IUnregisterWhatsapp,
  OperatorSessionChannel,
} from '@/services/call/interfaces/IRegisterQueues'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { PauseDto } from '@/services/pause/interfaces/pause.interface'
import { LogOut } from 'lucide-vue-next'

type CampaignExitMenuItem = MenuItem & {
  iconComponent: Component | string
}

const authStore = useAuthStore()
const connectivityStore = useConnectivityStore()
const workspace = useWorkspaceStore()
const pauseStatusStore = usePauseStatusStore()
const { confirmPauseCreation } = usePauseCreateConfirmDialog()
const campaignRegistrationStore = useCampaignRegistrationStore()
const callService = useCallService()
const campaignService = useCampaignService()
const chatService = useChatService()
const pauseService = usePauseService()
const {
  refreshChatRegistration,
  isVoiceRegistrationLost,
  isChatRegistrationLost,
  confirmCampaignAssignmentChange,
} = useCampaignRegistrationSync()
const { acceptLogout, isLogoutLoading } = useLogoutConfirm()
const toast = useToast()
const { t, locale } = useI18n()

const { shouldBlockWebphone, checkPermissions, ensureMicrophoneAccess, tooltipKey } =
  useWebphonePermissions()
const { isPaused, selectedPause, isSupervisorPause } = storeToRefs(pauseStatusStore)
const {
  isRegisteredInCampaign,
  isVoiceStateKnown,
  isVoiceRegistrationPending,
  isChatStatusLoading,
  isClassificationFormActive,
  canProcessScheduledCampaignExit,
  campaignAssignmentChanged,
} = storeToRefs(campaignRegistrationStore)
const pauseMenu = ref<InstanceType<typeof Menu> | null>(null)
const campaignExitMenu = ref<InstanceType<typeof Menu> | null>(null)
const pauses = ref<PauseDto[]>([])
const loadingAction = ref<'register' | 'unregister' | 'pause' | null>(null)
const confirmExitPauseVisible = ref(false)
const confirmScheduledCampaignExitVisible = ref(false)
const confirmExitPauseSource = ref<'pause' | 'campaign'>('pause')
const shouldLogoutAfterScheduledCampaignExit = ref(false)

const extensionNumber = computed(() => authStore.extension?.number || '')
const extensionDescription = computed(() => authStore.extension?.description || '-')
// authStore.userExtension refleja la extensión efectiva de la sesión (propia o
// elegida en el paso 2 del login); vacía si el usuario entró sin extensión.
// Un usuario puede abrir el webphone aunque no tenga campañas de voz asignadas;
// la extensión efectiva de la sesión sigue siendo la condición necesaria.
const hasAssignedExtension = computed(() => Boolean(authStore.userExtension))
const isExtensionUnavailable = computed(
  () => !authStore.webphoneAvailable || !hasAssignedExtension.value,
)
const isWebphoneDisconnected = computed(() => connectivityStore.isAnyServiceDisconnected)
const isWebphoneInteractionDisabled = computed(() => isExtensionUnavailable.value)
const shouldShowWebphoneDisconnectedStyle = computed(
  () =>
    isWebphoneInteractionDisabled.value ||
    isWebphoneDisconnected.value ||
    shouldBlockWebphone.value,
)

const webphoneTooltip = computed(() => t(tooltipKey.value))
const shouldShowWebphoneInfo = computed(
  () => shouldBlockWebphone.value || isWebphoneDisconnected.value,
)
const webphoneInfoTooltip = computed(() =>
  shouldBlockWebphone.value ? webphoneTooltip.value : t('header.webphone_no_connection'),
)

const webphoneButtonTooltip = computed(() => {
  // if (isWebphoneDisconnected.value) return t('header.webphone_no_connection')
  if (!hasAssignedExtension.value) return t('header.webphone_no_extension_assigned')
  return `${t('header.extension')}: ${extensionNumber.value}`
})

const extensionStatusLabel = computed(() =>
  isExtensionUnavailable.value
    ? t('header.extension_status.disconnected')
    : t('header.extension_status.connected'),
)

// const extensionNameWithStatus = computed(
//   () => `${extensionDescription.value} - ${extensionStatusLabel.value}`,
// )

const extensionNameWithStatus = computed(() => `${extensionDescription.value}`)

const activeWebphoneStatuses = new Set(['incoming', 'calling', 'connected', 'held'])
const hasActiveWebphoneCall = computed(() =>
  activeWebphoneStatuses.has(workspace.webphoneCallStatus),
)

const operatorCampaigns = computed<IOptionsCampaign[]>(() => {
  return workspace.campaigns
})

const hasVoiceCampaign = computed(() => workspace.hasVoiceCampaign)

// "Cargando" mientras ningún canal tiene todavía un estado confirmado por su
// fuente: la consulta de chat en vuelo, la ventana entre el POST de registro de
// voz y el primer evento de Asterisk, o el arranque sin QueueMemberGrouped aún.
// Solo se espera al evento si puede llegar: sin campaña de voz o sin extensión
// el agente no es miembro de ninguna cola y Asterisk no lo va a reportar nunca.
const isCampaignStatusLoading = computed(
  () =>
    isChatStatusLoading.value ||
    isVoiceRegistrationPending.value ||
    (hasVoiceCampaign.value && hasAssignedExtension.value && !isVoiceStateKnown.value),
)

// Sin extensión asignada, el usuario no puede atender campañas de llamadas: se
// excluyen del registro para que "Iniciar campaña" solo registre WhatsApp
// (regla de negocio de extensiones — ver getRegisterQueuePayload/enterCampaign).
const queueCampaigns = computed(() => {
  if (!hasAssignedExtension.value) return []

  return operatorCampaigns.value
    .filter((campaign) => campaign.type !== TypeCampaign.WHATSAPP)
    .filter((campaign) => Boolean(campaign.id))
    .map((campaign) => ({
      id: campaign.id,
      queue: campaign.queue ?? '',
    }))
})

const whatsappCampaignIds = computed(() =>
  operatorCampaigns.value
    .filter((campaign) => campaign.type === TypeCampaign.WHATSAPP)
    .map((campaign) => campaign.id)
    .filter(Boolean),
)

const campaignStatusTitle = computed(() =>
  isRegisteredInCampaign.value
    ? t('call.registered_in_campaign')
    : t('call.not_registered_in_campaign'),
)

const campaignStatusDescription = computed(() =>
  isRegisteredInCampaign.value ? t('call.ready_to_receive') : t('call.register_to_start'),
)

const campaignStatusRegisterText = computed(() => {
  const description = campaignStatusDescription.value
  const registerMatch = description.match(/registrarte|register/i)

  if (!registerMatch || registerMatch.index === undefined) {
    return {
      before: description,
      link: '',
      after: '',
    }
  }

  return {
    before: description.slice(0, registerMatch.index),
    link: registerMatch[0],
    after: description.slice(registerMatch.index + registerMatch[0].length),
  }
})

const currentPauseLabel = computed(() => {
  if (!isPaused.value) return t('header.actions.pause')

  const base = pauseStatusStore.activePauseLabel
    ? `${t('call.exit_pause')}: ${pauseStatusStore.activePauseLabel}`
    : t('call.active_pause')

  return pauseStatusStore.pauseElapsedLabel
    ? `${base} (${pauseStatusStore.pauseElapsedLabel})`
    : base
})

const currentDate = computed(() => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'

  return new Intl.DateTimeFormat(currentLocale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date())
})

const pausedColor = '#FFD900'
const activeCallColor = '#EA3A3D'
const pausedHeaderShadow = computed(() =>
  isPaused.value
    ? '0 0 0 1px rgba(255, 217, 0, 0.28), 0 8px 24px rgba(255, 217, 0, 0.05)'
    : undefined,
)

const handleWebphoneClick = () => {
  if (isWebphoneInteractionDisabled.value) return

  if (isWebphoneDisconnected.value) {
    if (authStore.showWebphone) {
      authStore.setShowWebphone(false)
    }
    return
  }

  if (hasActiveWebphoneCall.value) {
    authStore.setShowWebphone(true)
    workspace.toggleWebphonePipExpanded()
    return
  }

  authStore.toggleWebphone()
}

const refreshWebphonePermissions = () => {
  void checkPermissions()
}

const showHeaderToast = (severity: 'success' | 'info' | 'warn' | 'error', detail: string) => {
  toast.add({
    severity,
    summary: t(
      severity === 'success'
        ? 'general.success'
        : severity === 'error'
          ? 'general.error'
          : 'general.warning',
    ),
    detail,
    life: 3000,
  })
}

const refreshWhatsAppChatsAfterCampaignRegister = async () => {
  if (!authStore.userId || !workspace.hasWhatsAppCampaign) return

  try {
    const chatsResponse = await chatService.getChats({
      user_id: authStore.userId,
    })

    window.dispatchEvent(
      new CustomEvent('whatsapp-campaign-registered', {
        detail: { chatsResponse },
      }),
    )
  } catch {}
}

const loadCampaigns = async () => {
  if (workspace.hasLoadedCampaigns) return

  const campaigns = await campaignService.getAllCampaigns()
  workspace.setCampaigns(Array.isArray(campaigns) ? campaigns : [])
}

const getSessionIdentifier = () =>
  localStorage.getItem('oper_session_identifier') ||
  authStore.currentUser?.email ||
  authStore.userAlias ||
  ''

const notifyWhatsAppOnlyRegistration = () => {
  showHeaderToast('info', t('call.whatsapp_only_registration'))
}

const validateVoiceCampaignPrerequisites = async (canRegisterWhatsapp: boolean) => {
  if (!hasVoiceCampaign.value) return true

  if (!hasAssignedExtension.value || !authStore.webphoneAvailable) {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('warn', t('call.extension_session_required'))
    return false
  }

  try {
    const hasActiveSession = await authStore.validateActiveSession(getSessionIdentifier(), {
      force: true,
    })

    if (!hasActiveSession) {
      if (canRegisterWhatsapp) {
        notifyWhatsAppOnlyRegistration()
        return false
      }

      showHeaderToast('warn', t('call.extension_session_required'))
      return false
    }
  } catch {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('error', t('auth.session_check_error'))
    return false
  }

  const microphoneAccess = await ensureMicrophoneAccess()
  if (microphoneAccess.allowed) return true

  if (canRegisterWhatsapp) {
    notifyWhatsAppOnlyRegistration()
    return false
  }

  const messageKey =
    microphoneAccess.status === 'denied'
      ? 'call.microphone_access_required'
      : 'call.microphone_device_required'

  showHeaderToast('warn', t(messageKey))
  return false
}

const loadPauses = async () => {
  const result = await pauseService.getPauses({ page: 1, size: 100 })
  pauses.value = result.data ?? []
  pauseStatusStore.hydrateFromCatalog(pauses.value)
}

const getRegisterQueuePayload = (): IRegisterQueues | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  return {
    user_id: authStore.userId,
    protocol: 'PJSIP',
    extension: authStore.userExtension || extensionNumber.value,
    alias: authStore.userAlias || authStore.userName || extensionDescription.value,
    campaign_ids: queueCampaigns.value,
  }
}

const getOperatorSessionCallPayload = (): IOperatorSessionCall | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    alias: queuePayload.alias,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getOperatorSessionCallUnregisterPayload = (): IOperatorSessionCallUnregister | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getRegisterWhatsappPayload = (): IRegisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
    campaign_ids: whatsappCampaignIds.value,
  }
}

const getUnregisterWhatsappPayload = (): IUnregisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
  }
}

const getRegisteredCampaignIds = (
  callPayload: IOperatorSessionCall | IOperatorSessionCallUnregister | null,
  whatsappCampaignIds: string[] = [],
) => {
  return Array.from(
    new Set([
      ...(callPayload?.campaign_ids.map((campaign) => campaign.id) ?? []),
      ...whatsappCampaignIds,
    ]),
  )
}

const syncRegisteredCampaignUseIa = (campaignIds: string[]) => {
  workspace.setUseIa(campaignIds.some((campaignId) => workspace.getCampaignUseIa(campaignId)))
}

const clearRegisteredCampaignUseIa = (campaignIds: string[]) => {
  campaignIds.forEach((campaignId) => {
    workspace.setCampaignUseIa(campaignId, false)
  })
  workspace.setUseIa(false)
}

const enterCampaign = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  const canRegisterWhatsapp = Boolean(whatsappPayload)
  const canRegisterVoice = await validateVoiceCampaignPrerequisites(canRegisterWhatsapp)
  const callPayload =
    canRegisterVoice && queueCampaigns.value.length ? getOperatorSessionCallPayload() : null

  // Si la telefonia no puede registrarse pero existe WhatsApp, el payload sale solo con WhatsApp.
  // Si no hay ningun canal disponible, se bloquea antes de llamar al backend.
  if (!callPayload && !whatsappPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    const payload: IRegisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { campaign_ids: whatsappPayload.campaign_ids } } : {}),
    }

    const result = await callService.registerOperatorSession(payload, { notify: false })
    const failedChannels = result.failed_channels ?? []

    syncRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload?.campaign_ids ?? []),
    )

    // El registro de voz no se da por hecho acá: se marca como pendiente y lo
    // confirma el primer QueueMemberGrouped que publique el poll de colas.
    if (callPayload && !failedChannels.includes('CALL')) {
      campaignRegistrationStore.markVoiceRegistrationPending()
    }

    if (whatsappPayload && !failedChannels.includes('WHATSAPP')) {
      await refreshWhatsAppChatsAfterCampaignRegister()
    }
    await refreshChatRegistration()
    notifyRegistrationOutcome(failedChannels)
    if (campaignAssignmentChanged.value) confirmCampaignAssignmentChange()
  } catch {
    // El backend registra por cola y persiste las que sí entraron antes de
    // fallar: se vuelve a preguntar en vez de asumir que no quedó nada.
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * El registro no es todo-o-nada: el backend responde 200 mientras al menos un
 * canal haya entrado, con los que fallaron en `failed_channels`. Se avisa qué
 * quedó fuera en vez de un "error al entrar a campaña" que contradiría al canal
 * que sí quedó registrado.
 */
const notifyRegistrationOutcome = (failedChannels: OperatorSessionChannel[]) => {
  if (!failedChannels.length) {
    showHeaderToast('success', t('call.success_register_queues'))
    return
  }

  showHeaderToast(
    'warn',
    t(
      failedChannels.includes('CALL')
        ? 'call.partial_registration_voice_failed'
        : 'call.partial_registration_chat_failed',
    ),
  )
}

/**
 * Re-registra SOLO el canal de chat, sin tocar la voz: el backend registra CALL
 * y luego WHATSAPP, así que un fallo en el segundo deja al agente dentro de las
 * colas y fuera de sus campañas de WhatsApp.
 */
const retryChatRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  if (!whatsappPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, whatsapp: { campaign_ids: whatsappPayload.campaign_ids } },
      { notify: false },
    )
    // A diferencia de la voz, acá no hay heartbeat que confirme: campaign_logins
    // es la fuente y se puede leer de inmediato.
    await refreshWhatsAppChatsAfterCampaignRegister()
    await refreshChatRegistration()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * Re-registra SOLO el canal de voz, sin tocar el chat: se usa cuando Asterisk
 * dejó de reportar al agente pero su campaign_login sigue abierto, estado en el
 * que "Iniciar campaña" no aparece porque el chat lo sigue dando por registrado.
 */
const retryVoiceRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  // Sin fallback a WhatsApp: acá el chat ya está registrado, así que los avisos
  // deben hablar de lo que falta (extensión, sesión, micrófono).
  const canRegisterVoice = await validateVoiceCampaignPrerequisites(false)
  if (!canRegisterVoice) return

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallPayload() : null
  if (!callPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, call: callPayload },
      { notify: false },
    )
    // No se sincroniza el flag de IA: ya se resolvió en el registro original y
    // recalcularlo solo con las campañas de voz pisaría el valor de las de chat.
    campaignRegistrationStore.markVoiceRegistrationPending()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

const executeCampaignExit = async (isScheduledExit = false): Promise<boolean> => {
  if (hasActiveWebphoneCall.value) {
    if (!isScheduledExit) confirmScheduledCampaignExitVisible.value = true
    return false
  }

  if (isPaused.value) {
    confirmExitPauseSource.value = 'campaign'
    confirmExitPauseVisible.value = true
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallUnregisterPayload() : null
  const whatsappPayload = getUnregisterWhatsappPayload()

  loadingAction.value = 'unregister'
  let campaignExitCompleted = false
  try {
    const payload: IUnregisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { enabled: true } } : {}),
    }

    await callService.unregisterOperatorSession(payload, { notify: false })
    clearRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload ? whatsappCampaignIds.value : []),
    )

    // La baja de voz la confirma la ausencia del evento: si el QueueRemove no
    // prosperó en Asterisk el agente sigue en la cola y el heartbeat lo va a
    // seguir reportando como registrado, que es la verdad.
    campaignRegistrationStore.expectVoiceUnregistration()
    pauseStatusStore.clearPause()
    await refreshChatRegistration()
    campaignExitCompleted = true
    showHeaderToast('success', t('call.success_unregister_queues'))
    return true
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_unregister_queues'))
    return false
  } finally {
    loadingAction.value = null
    if (isScheduledExit) {
      campaignRegistrationStore.resolveScheduledCampaignExit(campaignExitCompleted)
    }
  }
}

const exitCampaign = () => executeCampaignExit()

const reRegisterCampaign = async () => {
  if (loadingAction.value) return
  const exited = await executeCampaignExit()
  if (exited) {
    await enterCampaign()
  }
}

const exitCampaignAndLogout = async () => {
  shouldLogoutAfterScheduledCampaignExit.value = hasActiveWebphoneCall.value
  const campaignExitCompleted = await executeCampaignExit()
  if (!campaignExitCompleted) {
    if (!hasActiveWebphoneCall.value) shouldLogoutAfterScheduledCampaignExit.value = false
    return
  }

  shouldLogoutAfterScheduledCampaignExit.value = false
  await acceptLogout()
}

const toggleCampaignExitMenu = (event: MouseEvent) => {
  campaignExitMenu.value?.toggle(event)
}

watch(
  [hasActiveWebphoneCall, canProcessScheduledCampaignExit],
  ([hasActiveCall, canProcessExit]) => {
    if (hasActiveCall || !canProcessExit || !campaignRegistrationStore.claimScheduledCampaignExit())
      return

    void executeCampaignExit(true).then((campaignExitCompleted) => {
      if (!campaignExitCompleted || !shouldLogoutAfterScheduledCampaignExit.value) return

      shouldLogoutAfterScheduledCampaignExit.value = false
      void acceptLogout()
    })
  },
  { flush: 'post' },
)

watch(isRegisteredInCampaign, (isRegistered) => {
  if (isRegistered || !shouldLogoutAfterScheduledCampaignExit.value || isLogoutLoading.value) return

  shouldLogoutAfterScheduledCampaignExit.value = false
  void acceptLogout()
})

const getActivePause = async (): Promise<ActivePauseSelection | null> => {
  if (selectedPause.value) return selectedPause.value

  // Solo persiste el id entre recargas: se resuelve contra el catálogo.
  if (!pauses.value.length) {
    await loadPauses()
  }

  return selectedPause.value
}

const setQueuePause = async (pause: ActivePauseSelection, pauseState: boolean) => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  if (pauseState) {
    try {
      await confirmPauseCreation()
    } catch {
      return
    }
  }

  loadingAction.value = 'pause'
  try {
    const result = await callService.pauseQueues({
      user_id: authStore.userId,
      pause: {
        id: pause.id,
        name: pause.name,
        description: pause.description,
      },
      is_paused: pauseState,
      extension: authStore.userExtension || extensionNumber.value,
      protocol: 'PJSIP',
    })

    if (!result.status) return

    if (pauseState) {
      pauseStatusStore.applySelfPause(pause)
    } else {
      pauseStatusStore.clearPause()
    }
  } finally {
    loadingAction.value = null
  }
}

const exitPause = async () => {
  // Único motivo de bloqueo: el origen de la pausa, que sale del PausedReason que
  // reporta Asterisk. Antes se bloqueaba por no tener la pausa en memoria, y eso
  // también pasa con pausas propias (recarga, otro navegador, POST que no volvió),
  // así que el agente no podía reanudar la suya.
  if (isSupervisorPause.value) {
    showHeaderToast('warn', t('call.supervisor_pause_locked'))
    return
  }

  // Reanudar no necesita la pausa de catálogo: el backend cierra el pause_log
  // abierto del usuario sin mirar el id y manda Reason vacío a Asterisk. Se envía
  // igual aunque no se haya podido resolver cuál era —el botón solo se pulsa
  // estando pausado y despausar es idempotente—: quedarse sin mandarlo dejaba al
  // agente pausado en Asterisk sin salida.
  const activePause = await getActivePause()
  await setQueuePause(activePause ?? { id: '', name: '', description: '' }, false)
}

const togglePauseMenu = async (event: MouseEvent) => {
  if (!isRegisteredInCampaign.value) return

  if (isPaused.value) {
    await exitPause()
    return
  }

  if (!pauses.value.length) {
    await loadPauses()
  }

  pauseMenu.value?.toggle(event)
}

const pauseMenuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = pauses.value.map((pause) => ({
    label: pause.name,
    command: () => void setQueuePause(pause, true),
  }))

  if (!items.length) {
    return [{ label: t('call.no_pauses_available'), disabled: true }]
  }

  return items
})

const campaignExitMenuItems = computed<CampaignExitMenuItem[]>(() => [
  {
    label: t('header.actions.end_campaign'),
    iconComponent: IconExitArrowCircle,
    command: () => void exitCampaign(),
  },
  {
    label: t('header.actions.end_campaign_and_logout'),
    iconComponent: LogOut,
    command: () => void exitCampaignAndLogout(),
  },
])

const confirmExitPauseTitle = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_campaign_title')
    : t('call.confirm_exit_pause_title'),
)

const confirmExitPauseMessage = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_from_campaign_message')
    : t('call.confirm_exit_pause_message'),
)

const confirmScheduledCampaignExitTitle = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.confirm_scheduled_campaign_exit_logout_title')
    : t('call.confirm_scheduled_campaign_exit_title'),
)

const confirmScheduledCampaignExitMessage = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.confirm_scheduled_campaign_exit_logout_message')
    : t('call.confirm_scheduled_campaign_exit_message'),
)

const scheduledCampaignExitToastMessage = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.campaign_exit_logout_scheduled')
    : t('call.campaign_exit_scheduled'),
)

const cancelExitPause = () => {
  confirmExitPauseVisible.value = false
}

const cancelScheduledCampaignExit = () => {
  shouldLogoutAfterScheduledCampaignExit.value = false
  confirmScheduledCampaignExitVisible.value = false
}

const confirmScheduledCampaignExit = () => {
  campaignRegistrationStore.scheduleCampaignExit(isClassificationFormActive.value)
  confirmScheduledCampaignExitVisible.value = false
  showHeaderToast('info', scheduledCampaignExitToastMessage.value)
}

const confirmExitPause = async () => {
  await exitPause()
  confirmExitPauseVisible.value = false
}

onMounted(() => {
  refreshWebphonePermissions()
  void loadCampaigns()
  void loadPauses()
  void refreshChatRegistration()
})

const buttonActions = computed<ButtonActions[]>(() => {
  if (!isRegisteredInCampaign.value) {
    return [
      {
        icon: IconIncomingArrowCircle,
        text: t('header.actions.start_campaign'),
        textIsVisible: 1,
        action: enterCampaign,
        condition: null,
        color: '#EA3A3D',
        disabled: isCampaignStatusLoading.value || loadingAction.value === 'register',
        loading: isCampaignStatusLoading.value || loadingAction.value === 'register',
      },
    ]
  }

  return [
    {
      icon: IconWaitingTime,
      text: currentPauseLabel.value,
      textIsVisible: isPaused.value ? 1 : 0,
      action: togglePauseMenu,
      condition: null,
      color: isPaused.value ? pausedColor : '#9770E5',
      disabled: isCampaignStatusLoading.value || loadingAction.value === 'pause',
      loading: loadingAction.value === 'pause',
    },
    {
      icon: IconExitArrowCircle,
      text: t('header.actions.end_campaign'),
      textIsVisible: 0,
      action: toggleCampaignExitMenu,
      condition: null,
      color: '#5AC2E8',
      disabled:
        isCampaignStatusLoading.value ||
        loadingAction.value === 'unregister' ||
        isLogoutLoading.value,
      loading: loadingAction.value === 'unregister' || isLogoutLoading.value,
    },
  ]
})
</script>

<template>
  <div class="hidden lg:block m-4">
    <div
      class="app-card-glass backdrop-blur-sm shadow-md flex flex-row justify-between items-center px-4 py-2.5 rounded-md transition-all duration-200"
      :style="{ boxShadow: pausedHeaderShadow }"
    >
      <!-- Left: Webphone + Extension info -->
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2">
          <!-- Webphone button -->
          <button
            type="button"
            v-tooltip.bottom="webphoneButtonTooltip"
            class="flex items-center gap-1.5 rounded-lg px-2 py-1.5 transition-all duration-200 active:scale-95"
            :class="[
              isWebphoneInteractionDisabled
                ? 'cursor-not-allowed opacity-50'
                : hasActiveWebphoneCall
                  ? 'cursor-pointer hover:bg-[#EA3A3D]/8'
                  : 'cursor-pointer hover:bg-[var(--p-primary-color)]/8',
            ]"
            :style="{
              color:
                hasActiveWebphoneCall && !shouldShowWebphoneDisconnectedStyle
                  ? activeCallColor
                  : undefined,
            }"
            :disabled="isWebphoneInteractionDisabled"
            @click="handleWebphoneClick"
          >
            <IconPhone
              class="h-9 w-9"
              :stroke-width="2"
              :class="
                shouldShowWebphoneDisconnectedStyle
                  ? 'text-gray-400'
                  : hasActiveWebphoneCall
                    ? 'text-[#EA3A3D]'
                    : 'text-[var(--p-primary-color)]'
              "
            />
            <span
              class="montserrat text-[11px] font-bold tabular-nums leading-none"
              :class="
                shouldShowWebphoneDisconnectedStyle
                  ? 'text-gray-400'
                  : hasActiveWebphoneCall
                    ? 'text-[#EA3A3D]'
                    : 'text-[var(--p-primary-color)]'
              "
            >
              {{ extensionNumber }}
            </span>
          </button>

          <button
            v-if="shouldShowWebphoneInfo"
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-50 dark:hover:bg-amber-400/10"
            v-tooltip.bottom="webphoneInfoTooltip"
            @mouseenter="refreshWebphonePermissions"
            @focus="refreshWebphonePermissions"
          >
            <IconInfo class="h-5 w-5" />
          </button>
        </div>

        <div class="h-8 w-px bg-gray-200 dark:bg-neutral-700" />

        <div class="flex flex-col leading-tight">
          <span
            class="montserrat text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-primary-color)]"
          >
            {{ extensionNameWithStatus }}
          </span>
          <span class="montserrat text-xs text-gray-400 dark:text-gray-500">
            {{ currentDate }}
          </span>
        </div>
      </div>

      <!-- Center: Campaign status -->
      <!-- <div
      :class="[
        'min-w-72 text-sm space-y-1 transition-transform duration-300',
        !isRegisteredInCampaign ? 'animate-campaign-pulse' : '',
      ]"
    >
      <template v-if="!isRegisteredInCampaign">
        <div class="text-center underline font-semibold text-[#EA3A3D] rounded-md">
          {{ campaignStatusTitle }}
        </div>

        <div class="montserrat font-normal text-xs">
          {{ campaignStatusDescription }}
        </div>
      </template>
    </div> -->

      <div class="flex items-center gap-2 mr-2">
        <div v-for="(buttonAction, index) in buttonActions" :key="index">
          <AppButton
            v-tooltip.bottom="buttonAction.text"
            severity="secondary"
            size="small"
            outlined
            rounded
            :block="false"
            :disabled="buttonAction.disabled"
            :loading="buttonAction.loading"
            :style="{
              color: buttonAction.disabled ? '#B2B2B2' : buttonAction.color,
              borderColor: buttonAction.disabled ? '#D1D5DB' : buttonAction.color,
            }"
            class="app-button-header-glass shadow-md min-w-10 hover:bg-white/50! dark:hover:bg-[var(--p-primary-600)]/50! text-xs! min-h-10 transition-all duration-200 hover:scale-105 active:scale-95 disabled:hover:scale-100"
            :class="!isRegisteredInCampaign && index === 0 ? 'animate-campaign-pulse' : ''"
            @click="buttonAction.action"
          >
            <template #icon-start>
              <component :is="buttonAction.icon" class="w-4 h-4" :stroke-width="1.5" />
              <div v-if="buttonAction.textIsVisible">{{ buttonAction.text }}</div>
            </template>
          </AppButton>
        </div>

        <AppNotificationSettings />

        <Menu
          ref="pauseMenu"
          :model="pauseMenuItems"
          :popup="true"
          class="!rounded-lg !border !border-white/40 !bg-white/20 !shadow-xl !backdrop-blur-xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-slate-900/40 [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-primary-50)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-primary-50)]"
        />
        <Menu
          ref="campaignExitMenu"
          :model="campaignExitMenuItems"
          :popup="true"
          class="!rounded-lg !border !border-white/40 !bg-white/20 !shadow-xl !backdrop-blur-xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-slate-900/40 [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-primary-50)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-primary-50)]"
        >
          <template #item="{ item, props }">
            <a
              v-bind="props.action"
              class="flex items-center gap-2 text-[var(--p-text-color)] dark:text-[var(--p-primary-50)]"
            >
              <component :is="item.iconComponent" class="h-4 w-4" :stroke-width="1.5" />
              <span class="text-[var(--p-text-color)] dark:text-[var(--p-primary-50)]">{{ item.label }}</span>
            </a>
          </template>
        </Menu>

        <div class="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
        <!-- <AppOptionSwitcher /> -->
        <AppThemeSwitcher />
        <AppDarkMode />
      </div>
    </div>

    <div
      v-if="!isRegisteredInCampaign"
      class="mt-1 flex min-h-7 items-center justify-center rounded-md border border-[#EA3A3D]/20 bg-[#EA3A3D]/10 px-3 py-1 text-center text-xs font-semibold text-[#EA3A3D] dark:border-[#EA3A3D]/30 dark:bg-[#EA3A3D]/15"
    >
      <span>
        {{ campaignStatusTitle }}. {{ campaignStatusRegisterText.before
        }}<button
          v-if="campaignStatusRegisterText.link"
          type="button"
          class="font-semibold cursor-pointer underline underline-offset-2 transition hover:text-[#C9272A] disabled:cursor-wait disabled:opacity-70"
          :disabled="isCampaignStatusLoading || loadingAction === 'register'"
          @click="enterCampaign"
        >
          {{ campaignStatusRegisterText.link }}</button
        >{{ campaignStatusRegisterText.after }}
      </span>
    </div>

    <div
      v-if="campaignAssignmentChanged"
      class="mt-1 flex min-h-7 flex-wrap items-center justify-center gap-x-1 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-1 text-center text-xs font-semibold text-[#B45309] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]"
    >
      <span>{{ t('call.campaign_assignment_changed') }}</span>
      <button
        type="button"
        class="cursor-pointer font-semibold underline underline-offset-2 transition hover:text-[#92400E] dark:hover:text-[#FCD34D]"
        @click="reRegisterCampaign"
      >
        {{ t('call.exit_and_register_again') }}
      </button>
    </div>

    <div
      v-if="isVoiceRegistrationLost"
      class="mt-1 flex min-h-7 flex-wrap items-center justify-center gap-x-1 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-1 text-center text-xs font-semibold text-[#B45309] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]"
    >
      <span>{{ t('call.voice_registration_lost') }}</span>
      <button
        type="button"
        class="cursor-pointer font-semibold underline underline-offset-2 transition hover:text-[#92400E] disabled:cursor-wait disabled:opacity-70 dark:hover:text-[#FCD34D]"
        :disabled="loadingAction === 'register'"
        @click="retryVoiceRegistration"
      >
        {{ t('call.retry_voice_registration') }}
      </button>
    </div>

    <div
      v-else-if="isChatRegistrationLost"
      class="mt-1 flex min-h-7 flex-wrap items-center justify-center gap-x-1 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-1 text-center text-xs font-semibold text-[#B45309] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]"
    >
      <span>{{ t('call.chat_registration_lost') }}</span>
      <button
        type="button"
        class="cursor-pointer font-semibold underline underline-offset-2 transition hover:text-[#92400E] disabled:cursor-wait disabled:opacity-70 dark:hover:text-[#FCD34D]"
        :disabled="loadingAction === 'register'"
        @click="retryChatRegistration"
      >
        {{ t('call.retry_chat_registration') }}
      </button>
    </div>

    <Dialog
      v-model:visible="confirmExitPauseVisible"
      modal
      :header="confirmExitPauseTitle"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ confirmExitPauseMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelExitPause"
          />
          <AppButton
            :label="t('call.exit_pause')"
            variant="danger"
            :block="false"
            :loading="loadingAction === 'pause'"
            @click="confirmExitPause"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="confirmScheduledCampaignExitVisible"
      modal
      :header="confirmScheduledCampaignExitTitle"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ confirmScheduledCampaignExitMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelScheduledCampaignExit"
          />
          <AppButton
            :label="t('call.confirm_scheduled_campaign_exit_continue')"
            variant="danger"
            :block="false"
            @click="confirmScheduledCampaignExit"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
@keyframes campaign-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.025);
  }
}

.animate-campaign-pulse {
  animation: campaign-pulse 1.4s ease-in-out infinite;
  transform-origin: center;
}

/* .animate-campaign-pulse {
  animation: campaign-pulse 1.4s ease-in-out infinite;
  transform-origin: center;
  filter: drop-shadow(0 0 1px rgba(234, 58, 61, 0.25));
} */
</style>
